import { Fragment, useState, JSX, useEffect } from 'react';
import { Avatar, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import { Encounter, Practitioner } from '@medplum/fhirtypes';
import { ResourceAvatar, useMedplumProfile } from '@medplum/react';
import { alert } from '@/services/alert';
import { EncounterStatus } from '@bariendo/lib/constants';
import { IconLock } from '@tabler/icons-react';

type SignAndLockEncounterButtonProps = {
  encounter?: Encounter;
  loading: boolean;
  closeSignModal?: boolean;
  onSign: (encounter?: Encounter) => void;
};

export function SignAndLockEncounterButton({
  encounter,
  loading,
  closeSignModal,
  onSign,
}: SignAndLockEncounterButtonProps): JSX.Element {
  const [opened, setOpened] = useState(false);
  const currentUser = useMedplumProfile() as Practitioner | undefined;

  const handleOpen = () => setOpened(true);
  const handleClose = () => setOpened(false);

  useEffect(() => {
    setOpened(false);
  }, [closeSignModal]);

  const handleSign = () => {
    if (!currentUser) {
      alert.error('No author reference informed');
      return;
    }

    onSign(encounter);
  };

  const providerName =
    (currentUser?.name && formatHumanName(currentUser.name[0])) || currentUser?.name?.[0]?.text || 'Unknown Provider';

  if (encounter?.status === EncounterStatus.FINISHED) {
    return <Fragment />;
  }

  return (
    <>
      <Button variant="default" bg="#228be6" c="white" fw={600} onClick={handleOpen}>
        Sign & lock
      </Button>

      <Modal
        opened={opened}
        onClose={handleClose}
        title="Sign & lock note"
        withCloseButton
        centered
        styles={{
          header: {
            borderBottom: '1px solid #DEE2E6',
            marginBottom: 'var(--mantine-spacing-md)',
          },
          title: { fontWeight: 600 },
        }}
      >
        <Stack gap="lg">
          <Stack gap="sm">
            <Text size="sm" fw={600}>
              Signing As
            </Text>
            <Group
              gap="sm"
              p="sm"
              style={{
                border: '1px solid #DEE2E6',
                borderRadius: 'var(--mantine-radius-md)',
              }}
            >
              {currentUser ? (
                <ResourceAvatar value={currentUser} size="sm" radius="xl" />
              ) : (
                <Avatar radius="xl" size="sm">
                  {providerName.charAt(0)}
                </Avatar>
              )}
              <Text size="sm" fw={500}>
                {providerName}
              </Text>
            </Group>
          </Stack>
          <Group justify="flex-end">
            <Button
              onClick={handleSign}
              radius="xl"
              loading={loading}
              disabled={loading}
              styles={{ root: { backgroundColor: '#228BE6', color: 'white' } }}
              leftSection={<IconLock size={16} />}
            >
              Sign & lock note
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
